import { useState, useEffect, useCallback, useMemo } from 'react';
import { InventoryItem } from '../../../types/inventory';
import {
  INV_STATUS_BRANCH_LABEL,
  INV_STATUS_WAREHOUSE_LABEL,
} from '../../../constants/inventory-status-labels';

export interface SelectedProduct {
  id: number;
  code: string;
  name: string;
  type: string;
  recordCode: string;
  status: string;
  location: string;
}

export interface UseInventoryTransferModalProps {
  open: boolean;
  selectedProductIds: string[];
  inventoryItems: InventoryItem[];
  locations: { id: number; name: string; type?: 'WAREHOUSE' | 'BRANCH' }[];
  onSuccess: () => void;
  onRemoveProduct?: (productId: string) => void;
}

export const useInventoryTransferModal = ({
  open,
  selectedProductIds,
  inventoryItems,
  locations,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSuccess,
  onRemoveProduct,
}: UseInventoryTransferModalProps) => {
  // Estados
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [destinationLocationId, setDestinationLocationId] = useState<string>('');
  const [transferNotes, setTransferNotes] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Preparar productos seleccionados a partir de los datos ya disponibles
  const prepareSelectedProducts = useCallback(() => {
    try {
      // Encontrar los items correspondientes en el inventario
      const formattedProducts: SelectedProduct[] = [];
      selectedProductIds.forEach((selectedId) => {
        const item = inventoryItems.find((item) => item.id === selectedId);
        if (item) {
          // Si tenemos originalId, lo usamos, de lo contrario intentamos convertir el id
          const originalId = item.originalId || parseInt(item.id);
          if (originalId && !isNaN(originalId)) {
            formattedProducts.push({
              id: originalId, // ID para API
              code: selectedId, // Código visible
              name: item.name || 'Sin nombre',
              type: item.type || 'Sin tipo',
              recordCode: item.recordCode || 'Sin registro',
              status: item.status || 'Desconocido',
              location: item.location || 'Sin ubicación',
            });
          } else {
            console.warn(`No se pudo determinar un ID numérico para ${selectedId}`);
          }
        } else {
          console.warn(`No se encontró item con id=${selectedId} en el inventario`);
        }
      });

      if (formattedProducts.length === 0) {
        setError('No se encontraron piezas para transferir. Seleccione piezas válidas.');
        return;
      }

      setSelectedProducts(formattedProducts);
    } catch (err) {
      console.error('Error preparando productos:', err);
      setError('Error al preparar las piezas para transferencia.');
    }
  }, [selectedProductIds, inventoryItems]);

  // Preparar los productos seleccionados utilizando los datos ya disponibles
  useEffect(() => {
    if (open && selectedProductIds.length > 0 && inventoryItems && inventoryItems.length > 0) {
      prepareSelectedProducts();
    } else {
      setActiveStep(0);
    }
  }, [open, selectedProductIds, inventoryItems, prepareSelectedProducts]);

  // Manejar cambio de paso
  const handleNext = useCallback(() => {
    setActiveStep((prevStep) => prevStep + 1);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((prevStep) => prevStep - 1);
  }, []);

  const handleReset = useCallback(() => {
    setActiveStep(0);
    setSelectedProducts([]);
    setDestinationLocationId('');
    setTransferNotes('');
    setReturnReason('');
    setError(null);
  }, []);

  // Manejar cambio de ubicación de destino
  const handleDestinationChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setDestinationLocationId(event.target.value);
  }, []);

  // Manejar cambio de notas
  const handleNotesChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setTransferNotes(event.target.value);
  }, []);

  // Manejar cambio de razón de retorno
  const handleReturnReasonChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setReturnReason(event.target.value);
  }, []);

  // Remover producto de la lista
  const handleRemoveProduct = useCallback(
    (productId: string) => {
      setSelectedProducts((prev) => prev.filter((p) => p.code !== productId));
      if (onRemoveProduct) {
        onRemoveProduct(productId);
      }
    },
    [onRemoveProduct],
  );

  // Obtener el nombre de la ubicación de destino
  const getDestinationLocationName = useCallback(() => {
    const location = locations.find((loc) => loc.id.toString() === destinationLocationId);
    return location ? location.name : 'Ubicación no encontrada';
  }, [locations, destinationLocationId]);

  // Detectar tipo de transferencia basado en los productos seleccionados
  const transferType = useMemo(() => {
    if (selectedProducts.length === 0) return null;

    // Buscar en inventoryItems para obtener información completa sobre ubicación
    const relevantItems = inventoryItems.filter((item) =>
      selectedProducts.some((p) => p.code === item.id),
    );

    if (relevantItems.length === 0) return null;

    // Verificar si los productos están en una ubicación de tipo BRANCH
    const firstItem = relevantItems[0];
    const currentLocation = locations.find(
      (loc) => loc.name === firstItem.locationName || loc.name === firstItem.location,
    );
    const isInBranch = currentLocation?.type === 'BRANCH';

    // Verificar si todos tienen status en sucursal o si están en branch con disponibilidad
    const allAssigned = selectedProducts.every((p) => p.status === INV_STATUS_BRANCH_LABEL);
    const allAvailableInBranch =
      isInBranch && relevantItems.every((item) => item.availabilityStatus === 'Disponible');

    // Si están asignados o en branch y disponibles, es transferencia desde sucursal
    if (allAssigned || allAvailableInBranch) {
      // Necesitamos verificar si el destino es warehouse o branch
      const destination = locations.find((loc) => loc.id.toString() === destinationLocationId);
      if (destination?.type === 'WAREHOUSE') {
        return 'BRANCH_TO_WAREHOUSE';
      } else if (destination?.type === 'BRANCH') {
        return 'BRANCH_TO_BRANCH';
      }
      // Si no hay destino seleccionado aún pero sabemos que es desde branch
      if (isInBranch) {
        return 'BRANCH_TRANSFER'; // Unknown destination but from branch
      }
    }

    // Si todos tienen status en almacén, es transferencia desde warehouse
    const allUnassigned = selectedProducts.every((p) => p.status === INV_STATUS_WAREHOUSE_LABEL);
    if (allUnassigned) {
      return 'WAREHOUSE_TO_BRANCH';
    }

    return null;
  }, [selectedProducts, destinationLocationId, locations, inventoryItems]);

  // Obtener la ubicación actual de los productos seleccionados
  const currentLocationName = useMemo(() => {
    if (selectedProducts.length === 0) return '';
    // Todos los productos deberían estar en la misma ubicación
    return selectedProducts[0]?.location || '';
  }, [selectedProducts]);

  // Filtrar ubicaciones según el tipo de transferencia
  const availableLocations = useMemo(() => {
    if (transferType === 'BRANCH_TO_BRANCH') {
      // Solo mostrar branches, excluyendo la sucursal actual
      return locations.filter((loc) => loc.type === 'BRANCH' && loc.name !== currentLocationName);
    } else if (transferType === 'BRANCH_TO_WAREHOUSE') {
      // Solo mostrar warehouses
      return locations.filter((loc) => loc.type === 'WAREHOUSE');
    } else if (transferType === 'WAREHOUSE_TO_BRANCH') {
      // Solo mostrar branches
      return locations.filter((loc) => loc.type === 'BRANCH');
    }
    // Si no se puede determinar, mostrar todas
    return locations;
  }, [transferType, locations, currentLocationName]);

  // Validar que no se transfiera a la misma sucursal
  const isSameBranchTransfer = useMemo(() => {
    if (transferType !== 'BRANCH_TO_BRANCH') return false;
    if (!destinationLocationId) return false;
    const destination = locations.find((loc) => loc.id.toString() === destinationLocationId);
    return destination?.name === currentLocationName;
  }, [transferType, destinationLocationId, locations, currentLocationName]);

  // Validar si se puede continuar al siguiente paso
  const canProceedToNext = useCallback(() => {
    switch (activeStep) {
      case 0:
        return selectedProducts.length > 0;
      case 1:
        if (!destinationLocationId) return false;
        if (isSameBranchTransfer) return false;
        // Para branch-to-warehouse, returnReason es recomendado pero no obligatorio
        return true;
      default:
        return true;
    }
  }, [activeStep, selectedProducts.length, destinationLocationId, isSameBranchTransfer]);

  return {
    activeStep,
    loading,
    selectedProducts,
    destinationLocationId,
    transferNotes,
    returnReason,
    error,
    transferType,
    availableLocations,
    isSameBranchTransfer,
    setLoading,
    setError,
    handleNext,
    handleBack,
    handleReset,
    handleDestinationChange,
    handleNotesChange,
    handleReturnReasonChange,
    handleRemoveProduct,
    canProceedToNext,
    getDestinationLocationName,
  };
};
